/**
 * ── A ALTURA DO HERÓI, POR PROPORÇÃO DECLARADA (leva A3 da bancada) ──────────
 *
 * V8 da régua: o herói tem proporção declarada, não `vh`. A altura era
 * `aspect-[4/5] sm:aspect-[8/3]` cravada no JSX; o dono quer escolhê-la. Três
 * degraus, cada um um par celular/tela em `aspect-ratio` — `media` é EXATAMENTE
 * o que estava no ar, e é o padrão de quem nunca mexeu.
 *
 * Sem import nenhum: o aluno lê isto no navegador. `min-h-[420px]` e o véu
 * (`veuDoHeroi`) não mudam: a régua mede a proporção entre 700 e 1200 de altura
 * de janela, onde o mínimo não morde.
 */
/*
 * `faixa` e `curta` (21/09): o dono pediu «opções de altura menores para o
 * hero». As três de antes começavam em 3:1 — a 1280 px, 427 px de altura, e o
 * piso de 420 px de `min-h` não deixava ir abaixo disso. As duas novas vêm
 * PRIMEIRO na lista, que é a ordem do seletor (da mais baixa à mais alta), e
 * trazem o seu próprio piso (`MINIMO_DO_HEROI`): com o de 420 elas seriam só
 * nomes.
 */
export const ALTURAS_DO_HEROI = ['faixa', 'curta', 'baixa', 'media', 'alta'] as const;
export type AlturaDoHeroi = (typeof ALTURAS_DO_HEROI)[number];

/** largura / altura, como o CSS escreve. */
export const ASPECTO_DO_HEROI: Record<AlturaDoHeroi, { celular: string; tela: string }> = {
  faixa: { celular: '16 / 9', tela: '5 / 1' },
  curta: { celular: '4 / 3', tela: '4 / 1' },
  baixa: { celular: '1 / 1', tela: '3 / 1' },
  media: { celular: '4 / 5', tela: '8 / 3' },
  alta: { celular: '3 / 4', tela: '2 / 1' },
};

/** Valor desconhecido (corpo antigo, chave inventada) cai em `media`. */
export function aspectoDoHeroi(altura: string | null | undefined): { celular: string; tela: string } {
  return ASPECTO_DO_HEROI[(altura ?? 'media') as AlturaDoHeroi] ?? ASPECTO_DO_HEROI.media;
}

/**
 * O piso de altura do herói, em px, por degrau — o `min-h` que era 420 cravado.
 *
 * O piso existe para o texto caber (título, chamada, botão e os controles do
 * carrossel) quando a janela é estreita e a proporção daria uma caixa rasa. Nos
 * degraus baixos o texto encolhe (`heroiCompacto`) e o piso desce junto.
 */
export const MINIMO_DO_HEROI: Record<AlturaDoHeroi, number> = {
  faixa: 200,
  curta: 260,
  baixa: 420,
  media: 420,
  alta: 420,
};

export function minimoDoHeroi(altura: string | null | undefined): number {
  return MINIMO_DO_HEROI[(altura ?? 'media') as AlturaDoHeroi] ?? 420;
}

/** Os degraus em que o texto do herói encolhe para caber. */
export function heroiCompacto(altura: string | null | undefined): boolean {
  return altura === 'faixa' || altura === 'curta';
}

/**
 * ── COMO A IMAGEM DE UM DESTAQUE ENTRA NO HERÓI (21/09) ─────────────────────
 *
 * O dono: *«as imagens da capa, quando são verticais, perdem a resolução quando
 * estão em destaque no hero»*. Medido em produção no mesmo dia: a capa do curso
 * em destaque tinha **450×686**. O herói tem 1280–2000 px de largura, e o
 * `object-fit: cover` estica a largura da imagem até à da caixa — **3 a 4× de
 * ampliação**. O servidor não tem pixel para dar; a regra dos derivados é nunca
 * ampliar (`media-derivados.ts`).
 *
 * A saída não é esticar melhor: é não esticar. `cartaz` mostra a capa INTEIRA,
 * no tamanho em que ela é nítida, ao lado do texto, sobre um fundo que é ela
 * mesma desfocada (desfocar esconde a falta de pixel, que é o que um fundo
 * precisa). `preencher` é o de sempre.
 *
 * `null` (automático): `cartaz` quando a imagem é vertical ou estreita demais
 * para cobrir a tela sem ampliar muito — e `preencher` quando o dono já cortou
 * (quem cortou quer preencher). Sem as dimensões, `preencher`: sem saber, não se
 * muda o desenho que existia.
 */
export const ENCAIXES_DA_CAPA = ['preencher', 'cartaz'] as const;
export type EncaixeDaCapa = (typeof ENCAIXES_DA_CAPA)[number];

/** Abaixo desta largura a imagem não cobre um herói de 1280 sem se ver o grão. */
export const LARGURA_MINIMA_PARA_PREENCHER = 1000;

export function encaixeDoSlide(s: {
  encaixe?: EncaixeDaCapa | null;
  enquadramento?: unknown;
  enquadramentoCelular?: unknown;
  imagemLargura?: number | null;
  imagemAltura?: number | null;
}): EncaixeDaCapa {
  if (s.encaixe) return s.encaixe;
  if (s.enquadramento || s.enquadramentoCelular) return 'preencher';
  const l = s.imagemLargura ?? 0;
  const a = s.imagemAltura ?? 0;
  if (!l || !a) return 'preencher';
  return a > l || l < LARGURA_MINIMA_PARA_PREENCHER ? 'cartaz' : 'preencher';
}

/**
 * ── O HERÓI DA COMUNIDADE (`D6`) — TRÊS DEGRAUS PRÓPRIOS, e a razão é medida ──
 *
 * A tentação era reusar `ALTURAS_DO_HEROI` acima, e ela está errada por um
 * número: o degrau `baixa` da vitrine é **1 / 1 no telemóvel**. Um quadrado de
 * 390×390 no topo da comunidade empurra o primeiro post para fora da dobra em
 * **todo** telefone — e a comunidade é a tela cuja doença diagnosticada é
 * *parecer morta*. Um banner que esconde a conversa é a mesma doença com melhor
 * aparência.
 *
 * A vitrine e a comunidade são dois trabalhos diferentes: lá o herói **é** a
 * oferta e pode ocupar a tela; aqui ele é a **entrada** de um lugar, e o lugar
 * é o que está abaixo dele. Por isso o degrau mais alto daqui (`cartaz`, 5 / 2)
 * é mais baixo que o mais baixo de lá.
 *
 * Os nomes são os do objecto e não os da escala — **Tarja · Capa · Cartaz** —
 * porque o tutor escolhe uma forma, e não um adjetivo: «baixa» obriga-o a
 * imaginar o resultado, «tarja» já é o resultado.
 *
 * Sem import nenhum, como o resto deste arquivo: o aluno lê isto no navegador —
 * e, na verdade, nem isso: o servidor resolve o par e manda-o pronto, para o
 * pedaço de entrada do app do aluno não pagar por uma tabela de três linhas.
 */
export const ALTURAS_DA_COMUNIDADE = ['tarja', 'capa', 'cartaz'] as const;
export type AlturaDaComunidade = (typeof ALTURAS_DA_COMUNIDADE)[number];

/** largura / altura, como o CSS escreve. A 1280 de palco: 183 · 320 · 512 px. */
export const ASPECTO_DA_COMUNIDADE: Record<
  AlturaDaComunidade,
  { celular: string; tela: string }
> = {
  tarja: { celular: '5 / 2', tela: '7 / 1' },
  capa: { celular: '3 / 2', tela: '4 / 1' },
  cartaz: { celular: '1 / 1', tela: '5 / 2' },
};

/** Valor desconhecido (corpo antigo, chave inventada) cai na `tarja`. */
export function aspectoDaComunidade(
  altura: string | null | undefined,
): { celular: string; tela: string } {
  return (
    ASPECTO_DA_COMUNIDADE[(altura ?? 'tarja') as AlturaDaComunidade] ?? ASPECTO_DA_COMUNIDADE.tarja
  );
}

/**
 * ── A MESMA PROPORÇÃO, COMO NÚMERO (leva 2 — «A Vitrine», o app) ────────────
 *
 * A web escreve `aspect-ratio: 4 / 5` no CSS e o navegador lê a fração. O React
 * Native não tem CSS: `<View style={{ aspectRatio }}>` quer um **número** —
 * largura ÷ altura. Esta função é a ponte, e ela existe **aqui**, no subpath
 * puro que o app já vai importar, por um motivo de camada: a proporção do herói
 * é forma-fato do core (`ASPECTO_DO_HEROI`), e o app **não** deve reparsear a
 * gramática `'L / A'` à mão — um `split('/')` solto na tela é o começo de uma
 * segunda leitura da mesma régua, e a régua de `VT1` proíbe exatamente isso
 * (`grep '1 / 1'|'4 / 5'|'3 / 4' apps/app/src` → 0).
 *
 * Recebe a string que o servidor resolveu (`aspectoDoHeroi(altura).celular`) e
 * devolve o número. Formato inesperado — string vazia, sem barra, com zero no
 * denominador — cai na proporção de `media` (4/5 = 0,8), nunca em `NaN`, `0` ou
 * `Infinity`: uma caixa de altura zero ou infinita é pior que a proporção
 * errada, e é o defeito que ninguém reporta porque «o app é assim».
 */
export function razaoDoAspecto(aspecto: string | null | undefined): number {
  const [l, a] = (aspecto ?? '').split('/').map((n) => Number(n.trim()));
  return l && a ? l / a : 4 / 5;
}
